/**
 * Route configuration
 * Centralizes all application routes for easier maintenance
 */

import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import App from '../App';

// Lazy load service pages
const Services = lazy(() => import('../pages/Services'));
const RoofRepairs = lazy(() => import('../pages/services/RoofRepairs'));
const RoofMaintenance = lazy(() => import('../pages/services/RoofMaintenance'));
const RoofReplacement = lazy(() => import('../pages/services/RoofReplacement'));
const SkylightsVentilation = lazy(() => import('../pages/services/SkylightsVentilation'));
const NewConstruction = lazy(() => import('../pages/services/NewConstruction'));

// Lazy load location pages
const HoustonRoofing = lazy(() => import('../pages/locations/HoustonRoofing'));
const AtascocitaRoofing = lazy(() => import('../pages/locations/AtascocitaRoofing'));
const WaldenLakeHouston = lazy(() => import('../pages/locations/WaldenLakeHouston'));
const TheWoodlandsRoofing = lazy(() => import('../pages/locations/TheWoodlandsRoofing'));
const KingwoodRoofing = lazy(() => import('../pages/locations/KingwoodRoofing'));
const HumbleRoofing = lazy(() => import('../pages/locations/HumbleRoofing'));
const SpringRoofing = lazy(() => import('../pages/locations/SpringRoofing'));
const ConroeRoofing = lazy(() => import('../pages/locations/ConroeRoofing'));
const AldineRoofing = lazy(() => import('../pages/locations/AldineRoofing'));

// Lazy load about and blog pages
const About = lazy(() => import('../pages/About'));
const OwensCorningPage = lazy(() => import('../pages/OwensCorningPage'));
const BookInspection = lazy(() => import('../pages/BookInspection'));
const Reviews = lazy(() => import('../pages/Reviews'));
const Blog = lazy(() => import('../pages/Blog'));
const BlogPost = lazy(() => import('../pages/BlogPost'));
const BlogCategory = lazy(() => import('../pages/BlogCategory'));

// Lazy load program pages (legacy hub)
const Programs = lazy(() => import('../pages/programs/Programs'));

// Lazy load discount program pages
const SeniorDiscount = lazy(() => import('../pages/programs/SeniorDiscount'));
const FirstResponders = lazy(() => import('../pages/programs/FirstResponders'));

// Lazy load referral program pages
const HomeownerReferrals = lazy(() => import('../pages/programs/HomeownerReferrals'));
const RealtorPartners = lazy(() => import('../pages/programs/RealtorPartners'));
const InsuranceAgentPartners = lazy(() => import('../pages/programs/InsuranceAgentPartners'));
const TradePartners = lazy(() => import('../pages/programs/TradePartners'));

// Lazy load partner portal pages
const SubmitReferral = lazy(() => import('../pages/SubmitReferral'));
const PartnerLogin = lazy(() => import('../pages/PartnerLogin'));
const PartnerDashboard = lazy(() => import('../pages/PartnerDashboard'));
const PartnerSubmitReferral = lazy(() => import('../pages/PartnerSubmitReferral'));

// Lazy load admin pages
const AdminLogin = lazy(() => import('../pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const Leads = lazy(() => import('../pages/admin/Leads'));
const AdminReferrals = lazy(() => import('../pages/admin/Referrals'));
const BlogPosts = lazy(() => import('../pages/admin/blog/BlogPosts'));
const BlogPostEditor = lazy(() => import('../pages/admin/blog/BlogPostEditor'));
const BlogPostPreview = lazy(() => import('../pages/admin/blog/BlogPostPreview'));
const Categories = lazy(() => import('../pages/admin/blog/Categories'));
const Tags = lazy(() => import('../pages/admin/blog/Tags'));
const Users = lazy(() => import('../pages/admin/Users'));
const Images = lazy(() => import('../pages/admin/Images'));
const Projects = lazy(() => import('../pages/admin/Projects'));
const ProjectEditor = lazy(() => import('../pages/admin/ProjectEditor'));
const AdminReviews = lazy(() => import('../pages/admin/Reviews'));
const AdminPartners = lazy(() => import('../pages/admin/Partners'));
const AdminPhotos = lazy(() => import('../pages/admin/Photos'));
const NotFound = lazy(() => import('../pages/NotFound'));

export interface RouteConfig {
  path: string;
  element: React.ReactNode;
  protected?: boolean;
}

// Core public routes
export const coreRoutes: RouteConfig[] = [
  { path: '/', element: <App /> },
  { path: '/about', element: <About /> },
  { path: '/owens-corning-preferred-contractor', element: <OwensCorningPage /> },
  { path: '/book-inspection', element: <BookInspection /> },
  { path: '/reviews', element: <Reviews /> },
];

// Service routes
export const serviceRoutes: RouteConfig[] = [
  { path: '/services', element: <Services /> },
  { path: '/services/roof-repairs', element: <RoofRepairs /> },
  { path: '/services/roof-maintenance', element: <RoofMaintenance /> },
  { path: '/services/roof-replacement', element: <RoofReplacement /> },
  { path: '/services/skylights-ventilation', element: <SkylightsVentilation /> },
  { path: '/services/new-construction', element: <NewConstruction /> },
];

// Location routes
export const locationRoutes: RouteConfig[] = [
  { path: '/houston-roofing', element: <HoustonRoofing /> },
  { path: '/roofing-the-woodlands-tx', element: <TheWoodlandsRoofing /> },
  { path: '/roofing-kingwood-tx', element: <KingwoodRoofing /> },
  { path: '/roofing-atascocita-tx', element: <AtascocitaRoofing /> },
  { path: '/roofing-humble-tx', element: <HumbleRoofing /> },
  { path: '/roofing-spring-tx', element: <SpringRoofing /> },
  { path: '/roofing-conroe-tx', element: <ConroeRoofing /> },
  { path: '/roofing-aldine-tx', element: <AldineRoofing /> },
  { path: '/roofing-walden-lake-houston-atascocita-tx', element: <WaldenLakeHouston /> },
];

// Blog routes
export const blogRoutes: RouteConfig[] = [
  { path: '/blog', element: <Blog /> },
  { path: '/blog/:slug', element: <BlogPost /> },
  { path: '/blog/category/:slug', element: <BlogCategory /> },
];

// Discount program routes
export const discountRoutes: RouteConfig[] = [
  { path: '/discounts', element: <SeniorDiscount /> }, // Hub defaults to senior discount page
  { path: '/discounts/senior', element: <SeniorDiscount /> },
  { path: '/discounts/first-responders', element: <FirstResponders /> },
];

// Referral program routes
export const referralRoutes: RouteConfig[] = [
  { path: '/referrals', element: <HomeownerReferrals /> }, // Hub defaults to neighbor referrals
  { path: '/referrals/neighbors', element: <HomeownerReferrals /> },
  { path: '/referrals/realtors', element: <RealtorPartners /> },
  { path: '/referrals/insurance-agents', element: <InsuranceAgentPartners /> },
  { path: '/referrals/trade-partners', element: <TradePartners /> },
];

// Legacy program routes (kept for backwards compatibility)
export const programRoutes: RouteConfig[] = [
  { path: '/programs', element: <Programs /> },
];

// Partner portal routes
export const partnerPortalRoutes: RouteConfig[] = [
  { path: '/submit-referral', element: <SubmitReferral /> },
  { path: '/partner/login', element: <PartnerLogin /> },
  { path: '/partner/dashboard', element: <PartnerDashboard /> },
  { path: '/partner/submit-referral', element: <PartnerSubmitReferral /> },
];

// Admin routes (protected)
export const adminRoutes: RouteConfig[] = [
  { path: '/admin/login', element: <AdminLogin />, protected: false },
  { path: '/admin/dashboard', element: <AdminDashboard />, protected: true },
  { path: '/admin/leads', element: <Leads />, protected: true },
  { path: '/admin/referrals', element: <AdminReferrals />, protected: true },
  { path: '/admin/blog/posts', element: <BlogPosts />, protected: true },
  { path: '/admin/blog/posts/new', element: <BlogPostEditor />, protected: true },
  { path: '/admin/blog/posts/:id/edit', element: <BlogPostEditor />, protected: true },
  { path: '/admin/blog/preview/:slug', element: <BlogPostPreview />, protected: false },
  { path: '/admin/blog/categories', element: <Categories />, protected: true },
  { path: '/admin/blog/tags', element: <Tags />, protected: true },
  { path: '/admin/users', element: <Users />, protected: true },
  { path: '/admin/images', element: <Images />, protected: true },
  { path: '/admin/projects', element: <Projects />, protected: true },
  { path: '/admin/projects/:id', element: <ProjectEditor />, protected: true },
  { path: '/admin/reviews', element: <AdminReviews />, protected: true },
  { path: '/admin/partners', element: <AdminPartners />, protected: true },
  { path: '/admin/photos', element: <AdminPhotos />, protected: true },
];

// Redirect routes
export const redirectRoutes: RouteConfig[] = [
  { path: '/services/emergency-repairs', element: <Navigate to="/services/roof-repairs" replace /> },
  { path: '/admin/posts', element: <Navigate to="/admin/blog/posts" replace /> },
  { path: '/admin/posts/new', element: <Navigate to="/admin/blog/posts/new" replace /> },
  { path: '/admin/posts/:id/edit', element: <Navigate to="/admin/blog/posts/:id/edit" replace /> },
  { path: '/admin/categories', element: <Navigate to="/admin/blog/categories" replace /> },
  { path: '/admin/tags', element: <Navigate to="/admin/blog/tags" replace /> },
  // Legacy program URL redirects
  { path: '/programs/senior-discount', element: <Navigate to="/discounts/senior" replace /> },
  { path: '/programs/first-responders', element: <Navigate to="/discounts/first-responders" replace /> },
  { path: '/programs/homeowner-referrals', element: <Navigate to="/referrals/neighbors" replace /> },
  { path: '/programs/realtor-partners', element: <Navigate to="/referrals/realtors" replace /> },
  { path: '/programs/insurance-agent-partners', element: <Navigate to="/referrals/insurance-agents" replace /> },
  { path: '/programs/trade-partners', element: <Navigate to="/referrals/trade-partners" replace /> },
];

// 404 route
export const notFoundRoute: RouteConfig = {
  path: '*',
  element: <NotFound />,
};

// Helper to generate Spanish route paths
export const toSpanishPath = (path: string): string => {
  if (path === '/') return '/es';
  return `/es${path}`;
};

// All public routes (English)
export const publicRoutes: RouteConfig[] = [
  ...coreRoutes,
  ...serviceRoutes,
  ...locationRoutes,
  ...blogRoutes,
  ...discountRoutes,
  ...referralRoutes,
  ...programRoutes,
  ...partnerPortalRoutes,
];

// Generate Spanish versions of public routes
export const spanishRoutes: RouteConfig[] = publicRoutes
  .filter((route) => !route.path.includes(':')) // Exclude dynamic routes for simplicity
  .map((route) => ({
    ...route,
    path: toSpanishPath(route.path),
  }));
